/**
 * 前端安全防护工具
 * 提供XSS防护、输入验证、敏感信息过滤等功能
 */

/**
 * XSS防护 - HTML转义
 * @param {string} str - 待转义的字符串
 * @returns {string} 转义后的字符串
 */
export function escapeHtml(str) {
  if (typeof str !== 'string') {
    return str
  }
  
  const htmlEscapes = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;'
  }
  
  return str.replace(/[&<>"'/]/g, (match) => htmlEscapes[match])
}

/**
 * XSS防护 - 清理HTML标签
 * @param {string} str - 待清理的字符串
 * @param {boolean} allowTags - 是否允许某些标签（默认false，全部清理）
 * @returns {string} 清理后的字符串
 */
export function stripHtmlTags(str, allowTags = false) {
  if (typeof str !== 'string') {
    return str
  }
  
  if (allowTags) {
    // 只允许安全的标签
    const allowedTags = ['p', 'br', 'strong', 'em', 'u', 'span']
    const tagPattern = new RegExp(
      `<(?!/?(${allowedTags.join('|')})\\s*/?>)[^>]+>`,
      'gi'
    )
    return str.replace(tagPattern, '')
  }
  
  // 移除所有HTML标签
  return str.replace(/<[^>]*>/g, '')
}

/**
 * XSS防护 - 清理危险脚本
 * @param {string} str - 待清理的字符串
 * @returns {string} 清理后的字符串
 */
export function sanitizeScript(str) {
  if (typeof str !== 'string') {
    return str
  }
  
  // 移除script标签
  let sanitized = str.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
  
  // 移除事件处理器（onclick, onerror等）
  sanitized = sanitized.replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '')
  sanitized = sanitized.replace(/\s*on\w+\s*=\s*[^\s>]*/gi, '')
  
  // 移除javascript:协议
  sanitized = sanitized.replace(/javascript:/gi, '')
  
  // 移除data:协议中的危险内容
  sanitized = sanitized.replace(/data:text\/html/gi, '')
  
  return sanitized
}

/**
 * 输入验证 - SQL注入防护（前端基础检查）
 * @param {string} str - 待检查的字符串
 * @returns {boolean} 是否包含可疑SQL语句
 */
export function detectSqlInjection(str) {
  if (typeof str !== 'string') {
    return false
  }
  
  const sqlPatterns = [
    /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|EXECUTE|UNION|SCRIPT)\b)/i,
    /(--|#|\/\*|\*\/|;)/,
    /(\bOR\b.*=.*)|(\bAND\b.*=.*)/i,
    /('|"|`).*(OR|AND).*=.*\1/i
  ]
  
  return sqlPatterns.some(pattern => pattern.test(str))
}

/**
 * 输入验证 - 路径遍历防护
 * @param {string} str - 待检查的字符串
 * @returns {boolean} 是否包含路径遍历字符
 */
export function detectPathTraversal(str) {
  if (typeof str !== 'string') {
    return false
  }
  
  const pathPatterns = [
    /\.\./,
    /\.\.\//,
    /\.\.\\/,
    /\/\.\./,
    /\\\.\./
  ]
  
  return pathPatterns.some(pattern => pattern.test(str))
}

/**
 * 输入验证 - 命令注入防护
 * @param {string} str - 待检查的字符串
 * @returns {boolean} 是否包含可疑命令
 */
export function detectCommandInjection(str) {
  if (typeof str !== 'string') {
    return false
  }
  
  const commandPatterns = [
    /[;&|`$(){}[\]]/,
    /\b(cat|ls|pwd|whoami|id|uname|ps|kill|rm|mv|cp|chmod|chown)\b/i,
    /\$\{/,
    /\$\(/
  ]
  
  return commandPatterns.some(pattern => pattern.test(str))
}

/**
 * 综合输入验证
 * @param {string} str - 待验证的字符串
 * @param {object} options - 验证选项
 * @returns {object} { valid: boolean, reason: string }
 */
export function validateInput(str, options = {}) {
  const {
    maxLength = 10000,
    allowHtml = false,
    checkSql = true,
    checkPath = true,
    checkCommand = true
  } = options
  
  if (typeof str !== 'string') {
    return { valid: false, reason: '输入必须是字符串类型' }
  }
  
  // 长度检查
  if (str.length > maxLength) {
    return { valid: false, reason: `输入长度超过限制（最大${maxLength}字符）` }
  }
  
  // SQL注入检查
  if (checkSql && detectSqlInjection(str)) {
    return { valid: false, reason: '输入包含可疑SQL语句' }
  }
  
  // 路径遍历检查
  if (checkPath && detectPathTraversal(str)) {
    return { valid: false, reason: '输入包含路径遍历字符' }
  }
  
  // 命令注入检查
  if (checkCommand && detectCommandInjection(str)) {
    return { valid: false, reason: '输入包含可疑命令字符' }
  }
  
  // HTML标签检查
  if (!allowHtml && /<[^>]+>/.test(str)) {
    return { valid: false, reason: '输入包含HTML标签' }
  }
  
  return { valid: true, reason: '' }
}

/**
 * 清理和验证输入
 * @param {string} str - 待清理的字符串
 * @param {object} options - 清理选项
 * @returns {string} 清理后的字符串
 */
export function sanitizeInput(str, options = {}) {
  if (typeof str !== 'string') {
    return str
  }
  
  const {
    escapeHtml: shouldEscape = true,
    stripTags = true,
    removeScripts = true,
    trim = true
  } = options
  
  let sanitized = str
  
  // 去除首尾空格
  if (trim) {
    sanitized = sanitized.trim()
  }
  
  // 移除脚本
  if (removeScripts) {
    sanitized = sanitizeScript(sanitized)
  }
  
  // 移除HTML标签
  if (stripTags) {
    sanitized = stripHtmlTags(sanitized)
  }
  
  // HTML转义
  if (shouldEscape) {
    sanitized = escapeHtml(sanitized)
  }
  
  return sanitized
}

/**
 * 敏感信息过滤 - 从对象中移除敏感字段
 * @param {object} obj - 待过滤的对象
 * @param {array} sensitiveFields - 敏感字段列表
 * @returns {object} 过滤后的对象
 */
export function filterSensitiveData(obj, sensitiveFields = []) {
  if (!obj || typeof obj !== 'object') {
    return obj
  }
  
  const defaultSensitiveFields = [
    'password',
    'pwd',
    'passwd',
    'secret',
    'token',
    'accessToken',
    'refreshToken',
    'apiKey',
    'apikey',
    'privateKey',
    'privatekey',
    'creditCard',
    'cardNumber',
    'cvv',
    'ssn',
    'socialSecurityNumber'
  ]
  
  const fieldsToFilter = [...defaultSensitiveFields, ...sensitiveFields]
  const filtered = Array.isArray(obj) ? [...obj] : { ...obj }
  
  function filterRecursive(data) {
    if (Array.isArray(data)) {
      return data.map(item => filterRecursive(item))
    } else if (data && typeof data === 'object') {
      const result = {}
      for (const key in data) {
        if (Object.prototype.hasOwnProperty.call(data, key)) {
          const lowerKey = key.toLowerCase()
          const isSensitive = fieldsToFilter.some(field => 
            lowerKey.includes(field.toLowerCase())
          )
          
          if (isSensitive) {
            result[key] = '***'
          } else {
            result[key] = filterRecursive(data[key])
          }
        }
      }
      return result
    }
    return data
  }
  
  return filterRecursive(filtered)
}

/**
 * 生成随机字符串（用于CSRF token、nonce等）
 * @param {number} length - 字符串长度
 * @returns {string} 随机字符串
 */
export function generateRandomString(length = 32) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  let result = ''
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return result
}

/**
 * 生成时间戳（毫秒）
 * @returns {number} 时间戳
 */
export function generateTimestamp() {
  return Date.now()
}

/**
 * 生成请求签名（简单HMAC模拟，实际应使用crypto库）
 * @param {string} method - HTTP方法
 * @param {string} url - 请求URL
 * @param {object} data - 请求数据
 * @param {string} timestamp - 时间戳
 * @param {string} nonce - 随机数
 * @param {string} secret - 密钥（从localStorage获取）
 * @returns {string} 签名字符串
 */
export function generateRequestSignature(method, url, data, timestamp, nonce, secret) {
  // 注意：这是简化版本，实际应使用真正的HMAC算法
  // 在生产环境中应使用crypto-js或Web Crypto API
  const message = `${method.toUpperCase()}|${url}|${JSON.stringify(data)}|${timestamp}|${nonce}`
  // 这里只是示例，实际应使用HMAC-SHA256
  return btoa(message + secret).substring(0, 32)
}

/**
 * 检查URL是否安全
 * @param {string} url - 待检查的URL
 * @returns {boolean} 是否安全
 */
export function isSafeUrl(url) {
  if (typeof url !== 'string' || !url) {
    return false
  }
  
  // 检查是否包含危险字符
  if (url.includes('<script') || url.includes('javascript:') || url.includes('data:text/html')) {
    return false
  }
  
  // 如果是相对路径（以/开头），认为是安全的
  if (url.startsWith('/')) {
    return true
  }
  
  // 尝试解析为完整URL
  try {
    // 如果是相对路径，尝试拼接当前origin
    let urlToCheck = url
    if (!url.includes('://')) {
      // 相对路径，尝试拼接baseURL
      const baseURL = process.env.VUE_APP_BASE_API || ''
      if (baseURL) {
        // 确保baseURL以/结尾，url不以/开头
        const base = baseURL.endsWith('/') ? baseURL.slice(0, -1) : baseURL
        const path = url.startsWith('/') ? url : '/' + url
        urlToCheck = base + path
      } else if (typeof window !== 'undefined') {
        // 如果没有baseURL，使用当前页面的origin
        urlToCheck = window.location.origin + (url.startsWith('/') ? url : '/' + url)
      } else {
        // 既没有baseURL也没有window，相对路径认为是安全的
        return true
      }
    }
    
    const urlObj = new URL(urlToCheck)
    // 只允许http和https协议
    if (!['http:', 'https:'].includes(urlObj.protocol)) {
      return false
    }
    
    return true
  } catch (e) {
    // 如果解析失败，但URL是相对路径，认为是安全的
    if (url.startsWith('/')) {
      return true
    }
    // 其他情况返回false
    return false
  }
}

/**
 * 深度清理对象（递归清理所有字符串值）
 * @param {any} data - 待清理的数据
 * @param {object} options - 清理选项
 * @returns {any} 清理后的数据
 */
export function deepSanitize(data, options = {}) {
  if (typeof data === 'string') {
    return sanitizeInput(data, options)
  } else if (Array.isArray(data)) {
    return data.map(item => deepSanitize(item, options))
  } else if (data && typeof data === 'object') {
    const sanitized = {}
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        sanitized[key] = deepSanitize(data[key], options)
      }
    }
    return sanitized
  }
  return data
}

export default {
  escapeHtml,
  stripHtmlTags,
  sanitizeScript,
  detectSqlInjection,
  detectPathTraversal,
  detectCommandInjection,
  validateInput,
  sanitizeInput,
  filterSensitiveData,
  generateRandomString,
  generateTimestamp,
  generateRequestSignature,
  isSafeUrl,
  deepSanitize
}

